// src/regex.ts
var REGEX_PATTERN = {
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/
};
var REGEX_EMAIl = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
var REGEX_LATIN_CHARACTERS = /[^A-Za-z\s]/g;
var REGEX_PHONE_NUMBER = /^(\+\d{1,3})?[\s.-]?(0?)(3[2-9]|5[6|8|9]|7[0|6-9]|8[0-6|8|9]|9[0-4|6-9])[0-9]{7}$/;
var REGEX_URL = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:\d+)?(\/[^\s]*)?$/;
var MAPBOX_REGEX = {
  ALPHANUMERIC: /[^a-zA-Z0-9]/g
};
var SOCIAL_MEDIA_REGEX = {
  REMOVE_LEADING_SLASH: /^\//,
  REMOVE_LEADING_AT: /^@/,
  SPLIT_QUERY_PARAMS: /\?.*$/
};
var UNICODE = {
  SPACE: " ",
  // Regular space character
  ZERO_WIDTH_SPACE: "\u200B"
  // Zero-width space character
};
var TEXT_WRAPPING_PATTERNS = {
  // Regex for trimming trailing spaces or tabs from a line
  TRAILING_SPACES: /[ \t]+$/g,
  // Regex for cutting text at exact width
  EXACT_WIDTH: (width) => new RegExp(`.{1,${width}}`, "g"),
  // Regex for wrapping text at word boundaries
  WORD_BOUNDARY: (width) => new RegExp(
    `.{1,${width}}([\\s${UNICODE.ZERO_WIDTH_SPACE}]+|$)|[^\\s${UNICODE.ZERO_WIDTH_SPACE}]+?([\\s${UNICODE.ZERO_WIDTH_SPACE}]+|$)`,
    "g"
  )
};
var USERNAME_PLACEHOLDER_REGEX = /@some\.one\b/;
var PROJECT_CODE_REGEX = /#code\b/;
var PLURALIZATION_REGEX = {
  // Words ending in s, x, sh, ch → add "es"
  ES_SUFFIX: /(s|x|sh|ch)$/i,
  // Words ending in y preceded by a consonant → change y to "ies"
  IES_SUFFIX: /[^aeiou]y$/i
};

// src/config.ts
var MAX_IMAGE_SIZE = 512e3;
var MAX_FILE_SIZE = 3 * 1024;
var MAX_CHATBOT_FILE_SIZE = 500;
var MAX_IMAGE_DIMENSION = 1e4;
var DEFAULT_OFFSET = 0;
var DEFAULT_LIMIT = 6;
var PROJECT_TIMELINE_LIMIT = 10;
var DEFAULT_USERS_LIMIT = 100;
var DEFAULT_PROJECTS_LIMIT = 100;
var DEFAULT_TRAININGS_LIMIT = 10;
var COPILOT_KIT_API_URL = "https://api.cloud.copilotkit.ai";
var FIREBASE_CONFIG = {
  STORAGE_API_URL: "https://firebasestorage.googleapis.com",
  // Firebase Storage API for file uploads
  IDENTITY_TOOLKIT_URL: "https://identitytoolkit.googleapis.com",
  // Firebase Authentication API
  SECURE_TOKEN_URL: "https://securetoken.googleapis.com",
  // Firebase Token validation API
  DOMAINS: {
    AUTH_POPUPS: "https://*.firebaseapp.com",
    // Firebase authentication popup windows
    SERVICES: "https://*.firebase.com"
    // Firebase service domains for iframes
  }
};
var CSP_SOURCES = {
  SELF: "'self'",
  NONE: "'none'",
  UNSAFE_INLINE: "'unsafe-inline'",
  UNSAFE_EVAL: "'unsafe-eval'",
  DATA: "data:",
  BLOB: "blob:",
  HTTPS: "https:"
};
var EXTERNAL_SERVICES = {
  // Google Services
  GOOGLE_STATIC: "https://www.gstatic.com",
  GOOGLE_APIS: "https://apis.google.com",
  GOOGLE_FONTS_API: "https://fonts.googleapis.com",
  GOOGLE_FONTS_STATIC: "https://fonts.gstatic.com",
  // Sentry
  SENTRY: "https://*.sentry.io",
  // Cloudflare Insights
  CLOUDFLARE_INSIGHTS: "https://static.cloudflareinsights.com"
};
var DEV_CSP_SOURCES = {
  HTTP_LOCALHOST: "http://localhost:*",
  HTTPS_LOCALHOST: "https://localhost:*"
};
var GOOGLE_IMAGE_RESOURCE = "https://lh3.googleusercontent.com";
var BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  "2XL": 1536
};
var DEBOUNCE_DELAY = 100;
var SELECT_CLOSE_DELAY = 150;

// src/input.ts
var INPUT_PHONE_NUMBER_LENGTH = 12;
var INPUT_ADDRESS_LENGTH = 200;
var INPUT_NAME_USER_LENGTH = 40;
var INPUT_JOB_TITLE_LENGTH = 32;
var INPUT_SOCIAL_NAME_LENGTH = 50;
var INPUT_SOCIAL_URL_LENGTH = 255;
var AGE_MIN = 18;
var AGE_MAX = 100;
var INPUT_PROJECT_NAME_LENGTH = 255;
var INPUT_ROLE_LENGTH = 50;
var INPUT_COMPANY_LENGTH = 50;
var INPUT_SCHOOL_NAME_LENGTH = 100;
var INPUT_SCHOOL_LOCATION_LENGTH = 100;
var INPUT_DEGREE_LENGTH = 50;
var INPUT_MAJOR_LENGTH = 100;
var CERTIFICATE_NAME_LENGTH = 255;
var CERTIFICATE_ISSUER_LENGTH = 250;
var CERTIFICATE_URL_LENGTH = 262;
var INPUT_TYPE = /* @__PURE__ */ ((INPUT_TYPE2) => {
  INPUT_TYPE2["TEXT"] = "text";
  INPUT_TYPE2["PASSWORD"] = "password";
  INPUT_TYPE2["EMAIL"] = "email";
  return INPUT_TYPE2;
})(INPUT_TYPE || {});

// ../types/dist/index.mjs
var TextFormat = /* @__PURE__ */ ((TextFormat2) => {
  TextFormat2["BOLD"] = "bold";
  TextFormat2["ITALIC"] = "italic";
  TextFormat2["UNDERLINE"] = "underline";
  TextFormat2["CODE"] = "code";
  return TextFormat2;
})(TextFormat || {});
var BlockFormat = /* @__PURE__ */ ((BlockFormat2) => {
  BlockFormat2["PARAGRAPH"] = "paragraph";
  BlockFormat2["HEADING_1"] = "heading-1";
  BlockFormat2["HEADING_2"] = "heading-2";
  BlockFormat2["BLOCK_QUOTE"] = "block-quote";
  BlockFormat2["CODE_BLOCK"] = "code-block";
  BlockFormat2["BULLETED_LIST"] = "bulleted-list";
  BlockFormat2["NUMBERED_LIST"] = "numbered-list";
  BlockFormat2["LIST_ITEM"] = "list-item";
  BlockFormat2["LINK"] = "link";
  return BlockFormat2;
})(BlockFormat || {});
var ActionGroup = /* @__PURE__ */ ((ActionGroup22) => {
  ActionGroup22["RESUME"] = "resume";
  ActionGroup22["USER"] = "user";
  ActionGroup22["PROJECT"] = "project";
  ActionGroup22["EMPLOYEE"] = "employee";
  ActionGroup22["TRAINING"] = "training";
  ActionGroup22["MENU"] = "menu";
  ActionGroup22["STATISTIC"] = "statistic";
  return ActionGroup22;
})(ActionGroup || {});
var WorkPlace = /* @__PURE__ */ ((WorkPlace2) => {
  WorkPlace2["LH"] = "LH";
  WorkPlace2["NT"] = "604";
  WorkPlace2["HPT"] = "HPT";
  WorkPlace2["TL18"] = "TL18";
  WorkPlace2["HOME"] = "Home";
  return WorkPlace2;
})(WorkPlace || {});
var UserGroup = /* @__PURE__ */ ((UserGroup2) => {
  UserGroup2["CHEF"] = "chef";
  UserGroup2["KITCHEN_MANAGER"] = "kitchen_manager";
  UserGroup2["ON_BEHALF_MANAGER"] = "on_behalf_manager";
  UserGroup2["BGH"] = "bgh";
  UserGroup2["HF"] = "hf";
  return UserGroup2;
})(UserGroup || {});

// ../icons/dist/chunk-VLOECE4N.mjs
import { jsx } from "react/jsx-runtime";
var BoldIcon = ({ ...props }) => /* @__PURE__ */ jsx(
  "svg",
  {
    width: "11",
    height: "15",
    viewBox: "0 0 11 15",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx(
      "path",
      {
        d: "M1.52734 6.41243C0.975059 6.41243 0.527344 6.86015 0.527344 7.41243C0.527344 7.96472 0.975059 8.41243 1.52734 8.41243V6.41243ZM1.52734 13.2458H0.527344C0.527344 13.7981 0.975059 14.2458 1.52734 14.2458L1.52734 13.2458ZM1.52734 1.5791V0.579102C0.975059 0.579102 0.527344 1.02682 0.527344 1.5791L1.52734 1.5791ZM1.52734 7.41243V8.41243H6.11068V7.41243V6.41243H1.52734V7.41243ZM6.11068 13.2458V12.2458H1.52734V13.2458V14.2458H6.11068V13.2458ZM9.02734 10.3291H8.02734C8.02734 11.3876 7.16922 12.2458 6.11068 12.2458V13.2458V14.2458C8.27379 14.2458 10.0273 12.4922 10.0273 10.3291H9.02734ZM6.11068 7.41243V8.41243C7.16922 8.41243 8.02734 9.27056 8.02734 10.3291H9.02734H10.0273C10.0273 8.16599 8.27379 6.41243 6.11068 6.41243V7.41243ZM1.52734 1.5791V2.5791H5.27734V1.5791V0.579102H1.52734V1.5791ZM5.27734 7.41243V6.41243H1.52734V7.41243V8.41243H5.27734V7.41243ZM8.19401 4.49577H7.19401C7.19401 5.55431 6.33589 6.41243 5.27734 6.41243V7.41243V8.41243C7.44046 8.41243 9.19401 6.65888 9.19401 4.49577H8.19401ZM5.27734 1.5791V2.5791C6.33589 2.5791 7.19401 3.43722 7.19401 4.49577H8.19401H9.19401C9.19401 2.33265 7.44046 0.579102 5.27734 0.579102V1.5791ZM1.52734 1.5791H0.527344V13.2458H1.52734H2.52734V1.5791H1.52734Z",
        fill: "currentColor"
      }
    )
  }
);
var BoldIcon_default = BoldIcon;

// ../icons/dist/chunk-7JN7J5VF.mjs
import { jsx as jsx2 } from "react/jsx-runtime";
var ItalicIcon = ({ ...props }) => /* @__PURE__ */ jsx2(
  "svg",
  {
    width: "10",
    height: "15",
    viewBox: "0 0 10 15",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx2(
      "path",
      {
        d: "M1.52734 13.2458H4.86068M4.86068 1.5791H8.19401M3.19401 13.2458L6.52734 1.5791",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var ItalicIcon_default = ItalicIcon;

// ../icons/dist/chunk-YI5HH2TO.mjs
import { jsx as jsx3 } from "react/jsx-runtime";
var UnderlineIcon = ({ ...props }) => /* @__PURE__ */ jsx3(
  "svg",
  {
    width: "13",
    height: "15",
    viewBox: "0 0 13 15",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx3(
      "path",
      {
        d: "M1.86035 13.2458H11.8604M3.52702 1.5791V6.5791C3.52702 8.42005 5.0194 9.91243 6.86035 9.91243C8.7013 9.91243 10.1937 8.42005 10.1937 6.5791V1.5791",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var UnderlineIcon_default = UnderlineIcon;

// ../icons/dist/chunk-WZST6H7X.mjs
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
var CodeIcon = ({ ...props }) => /* @__PURE__ */ jsxs(
  "svg",
  {
    width: "14",
    height: "10",
    viewBox: "0 0 14 10",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      /* @__PURE__ */ jsx4(
        "path",
        {
          d: "M0.233484 5.41211L4.73348 9.91211L5.79297 8.85412L2.34871 5.41211L5.79297 1.97084L4.73423 0.912109L0.233484 5.41211Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx4(
        "path",
        {
          d: "M13.4872 5.41211L8.98722 0.912109L7.92773 1.9701L11.372 5.41211L7.92773 8.85337L8.98647 9.91211L13.4872 5.41211Z",
          fill: "currentColor"
        }
      )
    ]
  }
);
var CodeIcon_default = CodeIcon;

// ../icons/dist/chunk-27UO2X23.mjs
import { jsx as jsx5 } from "react/jsx-runtime";
var ListIcon = ({ ...props }) => /* @__PURE__ */ jsx5(
  "svg",
  {
    width: "14",
    height: "11",
    viewBox: "0 0 14 11",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx5(
      "path",
      {
        d: "M4.36068 9.57845H12.694M4.36068 5.41178H12.694M4.36068 1.24512H12.694M1.02897 9.57845V9.58012L1.02734 9.58013V9.57845H1.02897ZM1.02897 5.41178V5.41345L1.02734 5.41346V5.41178H1.02897ZM1.02897 1.24512V1.24678L1.02734 1.2468V1.24512H1.02897Z",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);

// ../icons/dist/chunk-KJL2OQAY.mjs
import { jsx as jsx6 } from "react/jsx-runtime";
var ListOrderedIcon = ({ ...props }) => /* @__PURE__ */ jsx6(
  "svg",
  {
    width: "16",
    height: "17",
    viewBox: "0 0 16 17",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx6(
      "path",
      {
        d: "M6.19336 12.5785H14.5267M1.19336 11.4829V11.3285C1.19336 10.6381 1.753 10.0785 2.44336 10.0785H2.47714C3.14884 10.0785 3.69345 10.623 3.69345 11.2947C3.69345 11.5871 3.59852 11.8716 3.42309 12.1055L1.19336 15.0785L3.69336 15.0785M6.19336 8.41178H14.5267M6.19336 4.24512H14.5267M1.19336 2.57845L2.86003 1.74512V6.74512",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var ListOrderedIcon_default = ListOrderedIcon;

// ../icons/dist/chunk-OPMVV64A.mjs
import { jsx as jsx7, jsxs as jsxs2 } from "react/jsx-runtime";
var QuoteIcon = ({ ...props }) => /* @__PURE__ */ jsxs2(
  "svg",
  {
    width: "11",
    height: "9",
    viewBox: "0 0 11 9",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      /* @__PURE__ */ jsx7(
        "path",
        {
          d: "M2.50658 4.6227C1.36576 4.6227 0.595703 3.82859 0.595703 2.65211C0.595703 1.59329 1.47984 0.710938 2.6777 0.710938C3.98964 0.710938 4.95934 1.76976 4.95934 3.38741C4.95934 7.06388 2.27841 8.53447 0.595703 8.71094V7.09329C1.73652 6.88741 3.01995 5.74035 3.07699 4.47564C3.01994 4.50506 2.79178 4.6227 2.50658 4.6227Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx7(
        "path",
        {
          d: "M8.32476 4.6227C7.18394 4.6227 6.41388 3.82859 6.41388 2.65211C6.41388 1.59329 7.29802 0.710938 8.49588 0.710938C9.80782 0.710938 10.7775 1.76976 10.7775 3.38741C10.7775 7.06388 8.09659 8.53447 6.41388 8.71094V7.09329C7.5547 6.88741 8.83813 5.74035 8.89517 4.47564C8.83813 4.50506 8.60996 4.6227 8.32476 4.6227Z",
          fill: "currentColor"
        }
      )
    ]
  }
);
var QuoteIcon_default = QuoteIcon;

// ../icons/dist/chunk-TDEPRWP5.mjs
import { jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
var Heading1Icon = ({ ...props }) => /* @__PURE__ */ jsxs3(
  "svg",
  {
    width: "14",
    height: "12",
    viewBox: "0 0 14 12",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      /* @__PURE__ */ jsx8(
        "path",
        {
          d: "M6.17285 0.912109V5.72461H2.04785V0.912109H0.672852V11.9121H2.04785V7.09961H6.17285V11.9121H7.54785V0.912109H6.17285Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx8(
        "path",
        {
          d: "M13.0479 11.9121V3.66211H12.0166L10.2979 4.12273V5.54586L11.6729 5.17736V11.9121H13.0479Z",
          fill: "currentColor"
        }
      )
    ]
  }
);
var Heading1Icon_default = Heading1Icon;

// ../icons/dist/chunk-A43UZMWQ.mjs
import { jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
var Heading2Icon = ({ ...props }) => /* @__PURE__ */ jsxs4(
  "svg",
  {
    width: "15",
    height: "12",
    viewBox: "0 0 15 12",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      /* @__PURE__ */ jsx9(
        "path",
        {
          d: "M6.48535 0.912109V5.72461H2.36035V0.912109H0.985352V11.9121H2.36035V7.09961H6.48535V11.9121H7.86035V0.912109H6.48535Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx9(
        "path",
        {
          d: "M14.7354 6.24023C14.7354 4.81642 13.5041 3.66211 11.9854 3.66211C10.4666 3.66211 9.23535 4.81642 9.23535 6.24023H10.702L10.7064 6.14123C10.76 5.52317 11.3122 5.03711 11.9854 5.03711C12.6938 5.03711 13.2687 5.57611 13.2687 6.24023C13.2687 6.54205 13.1499 6.81842 12.9534 7.02948L9.23535 10.8424V11.9121H14.7354V10.5371H11.6436L14.0607 7.93148L14.1692 7.80773C14.5242 7.37323 14.7354 6.82942 14.7354 6.24023Z",
          fill: "currentColor"
        }
      )
    ]
  }
);
var Heading2Icon_default = Heading2Icon;

// ../icons/dist/chunk-QYIGSXVF.mjs
import { jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
var TerminalIcon = ({ ...props }) => /* @__PURE__ */ jsxs5(
  "svg",
  {
    width: "13",
    height: "12",
    viewBox: "0 0 13 12",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      /* @__PURE__ */ jsx10(
        "path",
        {
          d: "M6.75936 5.29744L1.98454 0.522623L0.860352 1.64522L4.51496 5.29744L0.860352 8.94887L1.98375 10.0723L6.75936 5.29744Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx10(
        "rect",
        {
          x: "6.98047",
          y: "9.52832",
          width: "5.87928",
          height: "1.52318",
          fill: "currentColor"
        }
      )
    ]
  }
);
var TerminalIcon_default = TerminalIcon;

// src/toolbar.ts
var getShortcut = (shortcut) => {
  if (typeof window === "undefined" || typeof navigator === "undefined")
    return "";
  const platform = navigator.userAgent || navigator.platform;
  const isMac = /Mac/i.test(platform);
  const isWindowsOrLinux = /Win/i.test(platform) || /Linux/i.test(platform);
  if (isMac) return `\u2318+${shortcut}`;
  if (isWindowsOrLinux) return `Ctrl+${shortcut}`;
  return "";
};
var TEXT_FORMATS = [
  {
    format: TextFormat.BOLD,
    icon: BoldIcon_default,
    label: "Bold",
    shortcut: getShortcut("B")
  },
  {
    format: TextFormat.ITALIC,
    icon: ItalicIcon_default,
    label: "Italic",
    shortcut: getShortcut("I")
  },
  {
    format: TextFormat.UNDERLINE,
    icon: UnderlineIcon_default,
    label: "Underline",
    shortcut: getShortcut("U")
  },
  {
    format: TextFormat.CODE,
    icon: CodeIcon_default,
    label: "Inline Code",
    shortcut: getShortcut("`")
  }
];
var BLOCK_FORMATS = [
  {
    format: BlockFormat.NUMBERED_LIST,
    icon: ListOrderedIcon_default,
    label: "Numbered List",
    shortcut: getShortcut("4")
  },
  {
    format: BlockFormat.BULLETED_LIST,
    icon: ListIcon,
    label: "Bullet List",
    shortcut: getShortcut("3")
  },
  {
    format: BlockFormat.HEADING_1,
    icon: Heading1Icon_default,
    label: "Heading 1",
    shortcut: getShortcut("1")
  },
  {
    format: BlockFormat.HEADING_2,
    icon: Heading2Icon_default,
    label: "Heading 2",
    shortcut: getShortcut("2")
  },
  {
    format: BlockFormat.CODE_BLOCK,
    icon: TerminalIcon_default,
    label: "Code Block",
    shortcut: getShortcut("L")
  },
  {
    format: BlockFormat.BLOCK_QUOTE,
    icon: QuoteIcon_default,
    label: "Quote",
    shortcut: getShortcut("Q")
  }
];
var MAX_CHARACTERS = {
  BIO: 300,
  DESCRIPTION: 5e3
};
var DEFAULT_RICH_TEXT_VALUE = [
  { type: BlockFormat.PARAGRAPH, children: [{ text: "" }] }
];

// src/messages.ts
var MESSAGES = {
  // Form Messages
  FIELD_REQUIRED: (fieldName) => `Please enter your ${fieldName}.`,
  EMAIL_NOT_ALLOWED: (email) => `${email} is not allowed to access this application.`,
  UNKNOWN_ERROR: "An unknown error occurred. Please try again.",
  EMAIL_PASSWORD_INVALID: "Email or password is incorrect. Please try again.",
  // API Errors
  GENERAL_ERROR: "Something went wrong. Please try again later.",
  FETCH_OPTIONS_DATA_ERROR: "Failed to fetch the data option. Please try again later.",
  // Resumes
  RESUME_ID_MISSING: "Resume ID is missing.",
  // Personal Information
  FULLNAME_LENGTH: "Full Name must be no longer than 40 characters.",
  PHONE_NUMBER_LENGTH: `Phone number must be no longer than ${INPUT_PHONE_NUMBER_LENGTH} digits.`,
  EMAIL_LENGTH: "Email must be no longer than 60 characters.",
  JOB_TITLE_LENGTH: "Job title must be no longer than 32 characters.",
  ADDRESS_LENGTH: "Address must be no longer than 200 characters.",
  BIO_LENGTH: `Bio must be no longer than ${MAX_CHARACTERS.BIO} characters.`,
  DAY_OR_BIRTH_INVALID: "The day of birth is invalid.",
  DATE_OF_BIRTH_REQUIRED: "Please select your day of birth.",
  DATE_OF_BIRTH_FUTURE: "Day of birth cannot be in the future.",
  UNDER_MINIMUM_AGE: "You must be at least 18 years old.",
  OVER_MAXIMUM_AGE: "Please select a valid date of birth.",
  UPDATE_PERSONAL_INFO_FAILED: "Can't update personal information! Please try again.",
  UPDATE_PERSONAL_INFO_SUCCESS: "Your personal information has been saved successfully.",
  // Experience
  COMPANY_REQUIRED: "Please enter the company name.",
  PROJECT_NAME_LENGTH: "The project name must be no longer than 255 characters.",
  COMPANY_LENGTH: "Company name must be no longer than 50 characters.",
  JOB_TITLE_REQUIRED: "Please enter the job title.",
  DESCRIPTION_LENGTH: "Description must be no longer than 5000 characters.",
  RESPONSIBILITIES_LENGTH: "Responsibilities must be no longer than 5000 characters.",
  ROLE_LENGTH: "Role must be no longer than 50 characters.",
  ROLE_REQUIRED: "Please select a role.",
  ORDER: "The order is invalid.",
  TECH_STACK_DUPLICATE: "Duplicate categories are not allowed.",
  PROJECT_SKILL_CATEGORY_REQUIRED: "Please select a skill category.",
  PROJECT_TECHNICAL_SKILLS_REQUIRED: "Please at least select a skill.",
  UPDATE_EXPERIENCE_SUCCESS: "Your experience has been saved successfully.",
  // Education
  SCHOOL_NAME_LENGTH: "School name must be no longer than 100 characters.",
  SCHOOL_MAJOR_DUPLICATE: "Duplicate school and major combination are not allowed.",
  SCHOOL_LOCATION_LENGTH: "School location must be no longer than 200 characters.",
  DEGREE_LENGTH: "Degree/Program must be no longer than 50 characters.",
  MAJOR_LENGTH: "Major must be no longer than 100 characters.",
  UPDATE_EDUCATION_SUCCESS: "Your education has been saved successfully.",
  PHONE_NUMBER_VALID: "Please enter a valid phone number.",
  WEBHOOKS_RECEIVED: "Webhook received.",
  // QueryParams
  INVALID_LIMIT_PARAMS: "Invalid the limit query parameters.",
  INVALID_OFFSET_PARAMS: "Invalid offset query parameters.",
  // Start Date validations
  START_DATE_INVALID: "Please enter a valid start date.",
  START_DATE_LATER_THAN_END: "Start date cannot be later than the end date.",
  START_DATE_FUTURE: "Start date cannot be in the future.",
  START_DATE_REQUIRED: "Please select the start date.",
  // End Date validations
  END_DATE_REQUIRED: "Please select the end date.",
  END_DATE_INVALID: "Please enter a valid end date.",
  END_DATE_EARLIER_THAN_START: "End date cannot be earlier than the start date.",
  END_DATE_FUTURE: "End date cannot be in the future.",
  DATES_CANNOT_BE_SAME: "Start date and end date cannot be the same.",
  // Experience array validation
  MIN_EXPERIENCE_REQUIRED: "Please add at least one work experience.",
  // Education array validation
  MIN_EDUCATION_REQUIRED: "Please add at least one education.",
  // Technical Skills
  SKILL_CATEGORY_REQUIRED: "Please select a skill category.",
  SKILL_CATEGORY_DUPLICATE: "Duplicate categories are not allowed.",
  TECHNICAL_SKILLS_REQUIRED: "Please at least select a skill.",
  UPDATE_TECHNICAL_SKILLS_SUCCESS: "Your skills have been saved successfully.",
  // Contact info
  SOCIAL_URL_INVALID: "The url is invalid format.",
  SOCIAL_NAME_INVALID: "The name is invalid format.",
  SOCIAL_NAME_REQUIRED: "Please enter the social name.",
  SOCIAL_URL_REQUIRED: "Please enter the social url.",
  SOCIAL_NAME_LENGTH: "Social network name must be no longer than 50 characters.",
  SOCIAL_URL_LENGTH: "URL must be no longer than 255 characters.",
  SOCIAL_NAME_DUPLICATE: "Duplicate social links are not allowed.",
  // Certification
  CERTIFICATE_NAME_REQUIRED: "Please enter the certification name.",
  CERTIFICATE_NAME_LENGTH: "Certification name must be no longer than 255 characters.",
  CERTIFICATE_ISSUER_REQUIRED: "Please enter the issuing organization.",
  CERTIFICATE_ISSUER_LENGTH: "Issuing organization must be no longer than 250 characters.",
  CERTIFICATE_ISSUE_DATE_REQUIRED: "Please enter the issue date.",
  CERTIFICATE_ISSUE_DATE_IN_FUTURE: "Issue date cannot be in the future.",
  CREDENTIAL_URL_REQUIRED: "Please enter the credential url.",
  CREDENTIAL_URL_INVALID: "The credential url is invalid format.",
  CREDENTIAL_URL_LENGTH: "Credential url must be no longer than 262 characters.",
  UPDATE_CERTIFICATION_SUCCESS: "Your certifications have been saved successfully.",
  // Option data
  OPTION_DATA_CONTEXT_ERROR: "useOptionData must be used within OptionDataProvider.",
  // Address Autocomplete
  ADDRESS_AUTOCOMPLETE_SEARCHING: "Searching...",
  ADDRESS_AUTOCOMPLETE_NO_RESULTS: "No results found. You can still use the entered address.",
  ADDRESS_AUTOCOMPLETE_TYPE_TO_SEARCH: "Type to search for addresses",
  ADDRESS_AUTOCOMPLETE_PLACEHOLDER: "604 Nui Thanh",
  // Empty message autocomplete
  CATEGORIES_EMPTY: "No categories found",
  SKILL_EMPTY: "No skills found",
  // Empty message autocomplete
  ROLE_EMPTY: "No roles found",
  USER_EMPTY: "No users found",
  ADDITIONAL_ROLE_EMPTY: "No additional roles found",
  // Download by CMD/CTRL + K
  DOWNLOAD_RESUME_SUCCESS: "Resume has been downloaded successfully.",
  DOWNLOAD_RESUME_FAILED: "Failed to download the resume. Please try again.",
  // Training
  TRAINING_TOPIC_REQUIRED: "Please enter the training topic.",
  PRESENTATION_TITLE_REQUIRED: "Please enter the presentation title.",
  PRESENTATION_DATE_INVALID: "Please enter a valid presentation date.",
  PRESENTATION_DATE_PAST: "Presentation date cannot be in the past.",
  PRESENTATION_TIME_OUTSIDE_WORKING_HOURS: "Presentation time must be within working hours (8:00 AM to 5:30 PM).",
  PRESENTATION_DATE_LATER_THAN_EST_END: "Presentation date must be on or before the topic end date.",
  // Download video
  DOWNLOAD_VIDEO_SUCCESS: "Video downloaded successfully.",
  DOWNLOAD_VIDEO_FAILED: "Failed to download the video. Please try again."
};
var IMAGE_PROCESSING_MESSAGE = {
  INVALID_FILE_TYPE: "Invalid file type. Please provide an image file.",
  INVALID_DIMENSIONS: "Invalid image dimensions.",
  CANVAS_CONTEXT_ERROR: "Failed to get canvas context.",
  CONVERSION_ERROR: "Failed to convert image to PNG.",
  LOAD_ERROR: "Failed to load image. File may be corrupted.",
  PROCESSING_TIMEOUT: "Image processing timeout.",
  URL_TO_BASE64_ERROR: (error) => `Error converting URL to base64: ${error}`,
  ARRAY_BUFFER_ERROR: (error) => `Failed to convert ArrayBuffer to base64: ${error}`,
  PNG_BASE64_ERROR: (error) => `Error converting to PNG base64: ${error}`,
  FETCH_ERROR: (statusText) => `Failed to fetch image: ${statusText}`
};
var MAPBOX_MESSAGE = {
  NO_ACCESS_TOKEN: "Address search is currently unavailable",
  NO_DATA: "Could not retrieve address information.",
  NO_SUGGESTIONS: "No matching addresses found.",
  FAILED_TO_FETCH_SUGGESTIONS: "Unable to load address suggestions, please try again."
};
var RESUME_MESSAGE = {
  FAILED_TO_FETCH_RESUMES: (error) => `Failed to fetch resume: ${error}`,
  FAILED_TO_FETCH_RESUME_DATA: (error) => `Failed to fetch resume data: ${error}`
};
var UPLOAD_FILE_MESSAGE = {
  EXCEED_FILE_SIZE: `File size exceeds the ${(MAX_FILE_SIZE / 1024).toFixed(0)}MB limit. Please choose a smaller file.`,
  EXCEED_GIVEN_FILE_SIZE: (fileSize) => `File size exceeds the ${fileSize}KB limit. Please choose a smaller file.`,
  INVALID_FILE_TYPE: (listFileType) => `File type not supported. Please upload the file with extensions: ${listFileType}.`,
  EXCEED_IMAGE_DIMENSION: `Image dimensions too large. Maximum allowed is ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION} pixels.`,
  CORRUPTED_IMAGE: "Failed to load image preview. The file might be corrupted."
};
var API_ERROR_MESSAGE = {
  INVALID_RESPONSE_FORMAT: "Invalid response format",
  COMMON_ERROR: "An error occurred.",
  NETWORK_ERROR: "Network error occurred.",
  MAXIMUM_RETRY_ATTEMPTS: "Maximum retry attempts reached."
};
var SEARCH_FORM_MESSAGE = {
  UNKNOWN_ACTION_TYPE: "Unknown action type! Please try again.",
  // Action messages
  EXECUTE_ACTION_SUCCESS: "Action performed successfully!",
  EXECUTE_ACTION_FAILED: "Failed to perform action. Please try again."
};
var CONFIRM_MESSAGE_CONFIG = {
  // Default label for confirm dialog,
  DEFAULT_CONFIRM_LABEL: "Yes",
  DEFAULT_CANCEL_LABEL: "Cancel",
  // Confirmation messages
  CONFIRM_DEACTIVATE_USER: {
    message: (username) => `Are you sure you want to deactivate ${username}?`,
    confirmLabel: "Deactivate"
  },
  CONFIRM_ARCHIVED_PROJECT: {
    message: (projectCode) => `Are you sure you want to archive ${projectCode}?`,
    confirmLabel: "Archive"
  }
};
var PROJECT_PAGE_MESSAGE = {
  FETCH_PROJECT_INFO_FAILED: "Failed to fetch project information. Please try again later."
};

// src/keyboardEvent.ts
var KEYBOARD_EVENT = {
  ARROW_DOWN: "ArrowDown",
  ARROW_UP: "ArrowUp",
  ENTER: "Enter",
  ESCAPE: "Escape",
  TAB: "Tab",
  KEYDOWN: "keydown",
  SPACE: " "
};
var KEYBOARD_SHORTCUT = {
  SEARCH: "K"
};

// src/common.ts
var COPYRIGHT = "\xA9 ceVBuilder 2025";
var EMPTY_FIELD_VALUE = "[...]";
var SEARCH_PLACEHOLDER = "What do you wanna do?";
var SCROLL_CONFIG = {
  BEHAVIOR: "smooth",
  BLOCK: "start",
  INLINE: "nearest",
  DELAY: 100,
  MAX_RETRIES: 3,
  RETRY_DELAY: 200,
  LAYOUT_STABLE_DELAY: 500
};
var PROJECT_NAV_LINKS = {
  INFORMATION: "#information",
  TECHNICAL_STACK: "#technical-stack",
  MEMBERS: "#members",
  ACTIVITIES: "#activities"
};
var ROLE_ITEMS = [
  { name: "CEO", data: { order: 0, color: null } },
  { name: "CTO", data: { order: 1, color: null } },
  { name: "Director of Engineering", data: { order: 2, color: null } },
  { name: "Product Owner", data: { order: 3, color: "#ED9121" } },
  { name: "Product Manager", data: { order: 4, color: null } },
  { name: "Project Manager", data: { order: 5, color: "#009966" } },
  { name: "Technical Advisor", data: { order: 6, color: "#6699CC" } },
  { name: "Technical Leader", data: { order: 7, color: "#92400E" } },
  { name: "Scrum Master", data: { order: 8, color: null } },
  { name: "UI/UX Designer", data: { order: 9, color: "#AE8846" } },
  { name: "Business Analyst", data: { order: 10, color: "#9400D3" } },
  { name: "System Analyst", data: { order: 11, color: null } },
  { name: "QA Engineer", data: { order: 12, color: "#9400D3" } },
  { name: "Test Engineer", data: { order: 13, color: "#613D8F" } },
  { name: "Automation Tester", data: { order: 14, color: null } },
  { name: "Full-Stack Developer", data: { order: 15, color: null } },
  { name: "Backend Developer", data: { order: 16, color: "#0095E4" } },
  { name: "Frontend Developer", data: { order: 17, color: "#8FBC8F" } },
  { name: "Mobile Developer", data: { order: 18, color: null } },
  { name: "Software Engineer", data: { order: 19, color: null } },
  { name: "DevOps Engineer", data: { order: 20, color: null } },
  { name: "Cloud Engineer", data: { order: 21, color: null } },
  { name: "AI/ML Engineer", data: { order: 22, color: null } },
  { name: "Data Engineer", data: { order: 23, color: null } }
];

// src/date.ts
var MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];
var MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
var DATE_CONSTANTS = {
  START_YEAR: 1900,
  MONTH_DISPLAY_LENGTH: 3
  // Number of characters to show for month abbreviation
};
var DATE_PICKER_DEFAULTS = {
  PLACEHOLDER: "Select date",
  DEFAULT_MODE: "month-year"
};
var DATE_FORMATS = {
  MONTH_YEAR: "MMMM yyyy",
  FULL_DATE: "dd/MM/yyyy",
  STANDARD: "DD/MM/YYYY",
  COMPACT: "D/M/YYYY",
  SHORT_MONTH_YEAR: "MMM YYYY",
  SHORT_MONTH_DAY_YEAR: "MMM D, YYYY",
  SHORT_MONTH_DAY_YEAR_TIME: "MMM DD, YYYY | hh:mm A",
  FULL_MONTH_DAY_YEAR: "MMMM D, YYYY",
  TIME_WITH_DATE: "hh:mm A [\u2022] MMM D, YYYY",
  AT_TIME_ON_DATE: "[at] hh:mm A [on] MMM D, YYYY",
  DATETIME_DISPLAY: "MMM D, YYYY hh:mm A",
  TIME_ONLY: "hh:mm A"
};
var DATE_PICKER_MODES = {
  DATE: "date",
  MONTH_YEAR: "month-year",
  DATE_DIALOG: "date-dialog",
  DATETIME: "datetime"
};
var DATE_RANGE_DISPLAY = {
  PRESENT: "Present"
};

// src/routes.ts
var ROUTES = {
  // Auth
  SIGN_IN: "/signin",
  HOME: "/",
  // Projects
  PROJECTS: "/projects",
  // Employees
  EMPLOYEES: "/employees",
  // Trainings
  EMPLOYEE_TRAININGS: "/trainings",
  // User
  RESUMES: "/resumes",
  RESUME: "/resume",
  // CV Builder step
  PERSONAL_INFORMATION: "/personal-information",
  EXPERIENCES: "/experiences",
  TECHNICAL_SKILLS: "/technical-skills",
  EDUCATION: "/education",
  SOCIAL_LINKS: "/social-links",
  CERTIFICATES: "/certificates",
  PREVIEW: "/preview",
  NOT_FOUND: "/not-found"
};

// src/resume.ts
var ResumeStep = {
  TEMPLATE_SELECTION: "TEMPLATE_SELECTION",
  PERSONAL_INFO: "PERSONAL_INFO",
  WORK_EXPERIENCE: "WORK_EXPERIENCE",
  SKILLS: "SKILLS",
  EDUCATION: "EDUCATION",
  SOCIAL_LINKS: "SOCIAL_LINKS",
  CERTIFICATIONS: "CERTIFICATIONS"
};
var CV_TEMPLATE_ID = {
  DEFAULT: "1",
  AGILITYIO: "2"
};
var DEFAULT_NAME_CV_TEMPLATE = "Untitled";
var PROGRESS_CONFIG = {
  MIN_VALUE: 0,
  MAX_VALUE: 100
};
var STEP_TO_ROUTE = {
  [ResumeStep.TEMPLATE_SELECTION]: ROUTES.HOME,
  [ResumeStep.PERSONAL_INFO]: ROUTES.PERSONAL_INFORMATION,
  [ResumeStep.WORK_EXPERIENCE]: ROUTES.EXPERIENCES,
  [ResumeStep.SKILLS]: ROUTES.TECHNICAL_SKILLS,
  [ResumeStep.EDUCATION]: ROUTES.EDUCATION,
  [ResumeStep.SOCIAL_LINKS]: ROUTES.SOCIAL_LINKS,
  [ResumeStep.CERTIFICATIONS]: ROUTES.CERTIFICATES
};

// src/image.ts
var BLUR_SRC = {
  DEFAULT: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAALCAYAAABCm8wlAAAAAXNSR0IArs4c6QAAARdJREFUKFMlkEtPwkAUhc903tMOpQ8Fgk/8u7om7tAVmqDgK2Jc+FM0LiGsmpYE7JhO7/be851zLll8PDkpBAQXaObn9xuD/hDnpyNQSkHmy7njlKMqS1hrkaY5OBdQSoExBnJzf+vOjk/AOUdZVsiyA0gp2yUhILPXmRsOjhAEBEAA55xXCyFai4e3R6ekQtzpemVd11ivVxiNLlrK+9fSNXgbWjDGvXK32yEMQ+z3e5DnzxdHgwBZkkFKjaqqYIzxpO12CzJdTBtbDA77kFKBUgatNTabDfI8B5ncTVwcxzDK+F80R03NoijQ6/VAxpOxi6IIWmnUfzW6SQLOKLQyUMqAXF5f+ZCRipClqQ/YiWNYE8IYi39l2lXsUsa7EgAAAABJRU5ErkJggg==",
  LANDING_DEFAULT: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkEHJ4CgABpQE5GAlAvAAAAABJRU5ErkJggg=="
};
var FALLBACK_SRC = {
  DEFAULT: "/images/image-not-available.webp",
  AVATAR: "/images/default-avatar.webp",
  COMING_SOON: "/images/image-coming-soon.webp"
};
var IMAGES = {
  DEFAULT: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQCKUbkvVbYTxHg_OQesfM8x6OoIbcMJSN3_w&s",
  LOGO_LIGHT_MODE: "/images/logo-light-mode.png",
  LOGO_DARK_MODE: "/images/logo-dark-mode.png",
  CV_TEMPLATE: "/images/cv-template.webp",
  AVATAR_DEFAULT: "/images/avatar.webp",
  AVATAR_DEFAULT_PNG: "/images/avatar.png",
  LANDING_BG: "/images/landing-background.webp",
  LANDING_BG_MOBILE: "/images/landing-bg-mobile.webp",
  LOGO_AGILITY: "/images/agility-logo.webp",
  APP_LOGO: "/images/app-logo.webp",
  LOGO_NOTFOUND: "/images/notfound-logo.webp",
  CHATBOT_PROCESSING: "/images/chatbot-processing.gif",
  LOGO_AGILITY_DARK_MODE: "/images/agility-logo-dark-mode.webp",
  SLOTH_SURFING: "/images/sloth-surfing.png"
};
var DEFAULT_ALLOWED_FORMATS = [
  { mimeType: "image/jpeg", extensions: [".jpg", ".jpeg"], label: "JPG, JPEG" },
  { mimeType: "image/png", extensions: [".png"], label: "PNG" },
  { mimeType: "image/webp", extensions: [".webp"], label: "WEBP" }
];

// src/auth.ts
var UserRole = /* @__PURE__ */ ((UserRole2) => {
  UserRole2["ADMIN"] = "admin";
  UserRole2["USER"] = "user";
  return UserRole2;
})(UserRole || {});
var UserStatus = /* @__PURE__ */ ((UserStatus2) => {
  UserStatus2["ACTIVE"] = "active";
  UserStatus2["INACTIVE"] = "inactive";
  return UserStatus2;
})(UserStatus || {});

// src/master.ts
var MasterCategoryType = /* @__PURE__ */ ((MasterCategoryType2) => {
  MasterCategoryType2["JOB_TITLES"] = "jobTitles";
  MasterCategoryType2["SCHOOLS"] = "schools";
  MasterCategoryType2["FIELDS_OF_STUDY"] = "fieldsOfStudy";
  MasterCategoryType2["DEGREES"] = "degrees";
  MasterCategoryType2["SOCIAL_LINKS"] = "socialLinks";
  MasterCategoryType2["SKILLS"] = "skills";
  MasterCategoryType2["CERTIFICATIONS"] = "certifications";
  MasterCategoryType2["STACKS"] = "stacks";
  return MasterCategoryType2;
})(MasterCategoryType || {});

// src/query.ts
var QUERY_KEY = {
  SEARCH: "search",
  SKILL: "skill",
  RETURN_URL: "returnUrl",
  TRAINING_ID: "trainingId"
};

// src/section.ts
var HERO_CONTENT = {
  title: "Welcome Home!",
  content: "Agility Space is an internal platform with integrating AI assistant to simplify workflows, providing insights into employees, projects, and training, along with a resume feature to support career growth."
};

// src/status.ts
var STATUS_CODE = /* @__PURE__ */ ((STATUS_CODE2) => {
  STATUS_CODE2[STATUS_CODE2["OK"] = 200] = "OK";
  STATUS_CODE2[STATUS_CODE2["CREATED"] = 201] = "CREATED";
  STATUS_CODE2[STATUS_CODE2["NO_CONTENT"] = 204] = "NO_CONTENT";
  STATUS_CODE2[STATUS_CODE2["NOT_MODIFIED"] = 304] = "NOT_MODIFIED";
  STATUS_CODE2[STATUS_CODE2["BAD_REQUEST"] = 400] = "BAD_REQUEST";
  STATUS_CODE2[STATUS_CODE2["UNAUTHORIZED"] = 401] = "UNAUTHORIZED";
  STATUS_CODE2[STATUS_CODE2["FORBIDDEN"] = 403] = "FORBIDDEN";
  STATUS_CODE2[STATUS_CODE2["NOT_FOUND"] = 404] = "NOT_FOUND";
  STATUS_CODE2[STATUS_CODE2["METHOD_NOT_ALLOWED"] = 405] = "METHOD_NOT_ALLOWED";
  STATUS_CODE2[STATUS_CODE2["TOO_MANY_REQUESTS"] = 429] = "TOO_MANY_REQUESTS";
  STATUS_CODE2[STATUS_CODE2["INTERNAL_SERVER_ERROR"] = 500] = "INTERNAL_SERVER_ERROR";
  STATUS_CODE2[STATUS_CODE2["SERVICE_UNAVAILABLE"] = 503] = "SERVICE_UNAVAILABLE";
  return STATUS_CODE2;
})(STATUS_CODE || {});
var STATUS_MESSAGES = {
  [400 /* BAD_REQUEST */]: "Bad request. Please check your input.",
  [401 /* UNAUTHORIZED */]: "Unauthorized. Please provide valid credentials.",
  [403 /* FORBIDDEN */]: "Forbidden. You don't have access to this resource.",
  [404 /* NOT_FOUND */]: "Resource not found.",
  [405 /* METHOD_NOT_ALLOWED */]: "Method not allowed on this endpoint.",
  [429 /* TOO_MANY_REQUESTS */]: "Too many requests. Please try again later.",
  [500 /* INTERNAL_SERVER_ERROR */]: "Internal server error. Please try again later.",
  [503 /* SERVICE_UNAVAILABLE */]: "Service is currently unavailable. Please try again later."
};
var CLERK_STATUS_CODE = /* @__PURE__ */ ((CLERK_STATUS_CODE2) => {
  CLERK_STATUS_CODE2["USER_BANNED"] = "user_banned";
  return CLERK_STATUS_CODE2;
})(CLERK_STATUS_CODE || {});
var CLERK_STATUS_MESSAGES = {
  ["user_banned" /* USER_BANNED */]: "Your account is deactivated. Please contact the administrator for assistance."
};

// src/theme.ts
var THEME = {
  LIGHT: "light",
  DARK: "dark"
};

// ../icons/dist/chunk-4XEQX52D.mjs
import { jsx as jsx11 } from "react/jsx-runtime";
var FirstLineIcon = ({ ...props }) => /* @__PURE__ */ jsx11(
  "svg",
  {
    width: "20",
    height: "21",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx11(
      "path",
      {
        d: "M3.751 3.436c-.649.098-1.143.442-1.371.953-.195.438-.197.453-.197 2.244 0 1.473.007 1.675.06 1.882.178.696.613 1.13 1.309 1.309.206.053.408.059 1.865.059 1.45 0 1.659-.006 1.862-.058.696-.179 1.134-.616 1.312-1.31.053-.207.059-.408.059-1.865 0-1.454-.006-1.659-.059-1.863-.179-.697-.574-1.102-1.274-1.306-.176-.052-.39-.06-1.784-.067-.871-.004-1.673.005-1.782.022m7.599.461c-.36.166-.522.619-.354.989.073.16.273.335.437.383.077.023 1.18.031 3.219.024l3.103-.01.139-.092a.705.705 0 0 0 .333-.557c.017-.236-.034-.402-.172-.559-.225-.256 0-.24-3.472-.24-2.947 0-3.106.003-3.233.062M6.995 4.936c.078.028.113.067.14.159.023.081.032.629.025 1.629l-.01 1.508-.076.075-.076.076H3.835l-.076-.076-.076-.075-.01-1.508c-.011-1.636-.003-1.729.153-1.785.13-.048 3.032-.05 3.169-.003m4.355 3.127a.757.757 0 0 0-.354.989.81.81 0 0 0 .437.384c.077.022 1.18.03 3.219.024l3.103-.01.139-.092a.706.706 0 0 0 .333-.557c.017-.236-.034-.402-.172-.559-.225-.256 0-.241-3.472-.241-2.947 0-3.106.004-3.233.062M2.183 12.23c-.359.166-.521.619-.353.989a.8.8 0 0 0 .437.383c.078.023 2.673.031 7.802.024l7.686-.009.139-.092a.776.776 0 0 0 .306-.874.786.786 0 0 0-.383-.422c-.128-.058-.456-.061-7.817-.061-7.408 0-7.688.002-7.817.062m0 4.167c-.359.166-.521.619-.353.989a.8.8 0 0 0 .437.383c.078.023 2.673.031 7.802.024l7.686-.01.139-.092a.775.775 0 0 0 .306-.874.793.793 0 0 0-.383-.422c-.128-.058-.456-.06-7.817-.06-7.408 0-7.688.002-7.817.062",
        fillRule: "evenodd"
      }
    )
  }
);

// ../icons/dist/chunk-ISTU4P5D.mjs
import { jsx as jsx12 } from "react/jsx-runtime";
var UserBoxIcon = (props) => /* @__PURE__ */ jsx12(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx12(
      "path",
      {
        d: "M17.028 0C21.396 0 24 2.604 24 6.972v10.056c0 3.37-1.548 5.686-4.272 6.563-.792.276-1.704.41-2.7.41H6.972c-.996 0-1.908-.132-2.7-.41C1.548 22.716 0 20.4 0 17.029V6.972C0 2.604 2.604 0 6.972 0zM12 17.962c-3.24 0-5.987 1.62-7.163 3.925h-.015l-.022-.011v.321h14.4v-.326l-.038.015C18 19.596 15.24 17.962 12 17.962M12 7.2a4.29 4.29 0 0 0-4.296 4.296A4.305 4.305 0 0 0 12 15.804a4.305 4.305 0 0 0 4.296-4.308A4.29 4.29 0 0 0 12 7.2",
        fill: "currentColor"
      }
    )
  }
);

// ../icons/dist/chunk-2ELTSFQX.mjs
import { jsx as jsx13 } from "react/jsx-runtime";
var FileIcon = ({ ...props }) => /* @__PURE__ */ jsx13(
  "svg",
  {
    width: "16",
    height: "21",
    viewBox: "0 0 16 21",
    fill: "currentColor",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx13("path", { d: "M14 20.5664H2C0.89543 20.5664 0 19.671 0 18.5664V2.56641C0 1.46184 0.89543 0.566406 2 0.566406H9C9.0109 0.566879 9.02167 0.568899 9.032 0.572406C9.04177 0.575423 9.05182 0.577434 9.062 0.578406C9.15019 0.584059 9.23726 0.601204 9.321 0.629406L9.349 0.638406C9.37167 0.646088 9.39373 0.655447 9.415 0.666406C9.52394 0.71483 9.62321 0.782591 9.708 0.866406L15.708 6.86641C15.7918 6.95119 15.8596 7.05046 15.908 7.15941C15.918 7.18141 15.925 7.20441 15.933 7.22741L15.942 7.25341C15.9699 7.3368 15.9864 7.42359 15.991 7.51141C15.9926 7.52059 15.9949 7.52962 15.998 7.53841C15.9998 7.54763 16.0004 7.55703 16.0001 7.56641V18.5664C16.0001 19.671 15.1046 20.5664 14 20.5664ZM2 2.56641V18.5664H14V8.56641H9C8.44772 8.56641 8 8.11869 8 7.56641V2.56641H2ZM10 3.98041V6.56641H12.586L10 3.98041Z" })
  }
);
var FileIcon_default = FileIcon;

// src/icon.ts
var SEARCH_FORM_HEADING_ICONS = {
  [ActionGroup.RESUME]: FileIcon_default,
  [ActionGroup.USER]: UserBoxIcon,
  [ActionGroup.PROJECT]: FirstLineIcon
};

// src/action.ts
var DEFAULT_EMAIL_DOMAIN_LIST = [
  "@asnet.com.vn",
  "@agilityio.com"
];
var DEFAULT_EMAIL_DOMAIN = DEFAULT_EMAIL_DOMAIN_LIST[0];
var ActionGroup2 = /* @__PURE__ */ ((ActionGroup3) => {
  ActionGroup3["RESUMES"] = "RESUMES";
  ActionGroup3["USERS"] = "USERS";
  ActionGroup3["PROJECTS"] = "PROJECTS";
  return ActionGroup3;
})(ActionGroup2 || {});

// src/time.ts
var DEBOUNCE_SEARCH_INPUT = 500;
var WORKING_DAYS = [1, 2, 3, 4, 5];
var WORKING_HOURS_PER_DAY = 8;
var MORNING_START = 8;
var MORNING_END = 12;
var AFTERNOON_START = 13.5;
var AFTERNOON_END = 17.5;
var HALF_DAY_HOURS = 4;
var HALF_DAY = 0.5;
var FULL_DAY = 1;

// src/cookies.ts
var COOKIE_KEYS_SHARED = {
  EMAIL_USER_SHARED: "email_user_shared"
};
var COOKIES_TOKEN_SHARE_CONFIG = {
  sameSite: "Lax",
  secure: true
};

// src/place.ts
var WORK_PLACES_TEXT = {
  [WorkPlace.LH]: "Lighthouse Building",
  [WorkPlace.NT]: "604 Nui Thanh",
  [WorkPlace.HPT]: "Ho Phi Tich",
  [WorkPlace.TL18]: "Trung Luong 18",
  [WorkPlace.HOME]: "Work from home"
};

// src/employee.ts
var USER_GROUP_LABEL = {
  [UserGroup.BGH]: "big game hunter",
  [UserGroup.CHEF]: "chef",
  [UserGroup.HF]: "hell fire",
  [UserGroup.ON_BEHALF_MANAGER]: "on behalf manager",
  [UserGroup.KITCHEN_MANAGER]: "kitchen manager"
};

// src/chatbot.ts
var COPILOT_LABEL = {
  TITLE: "Agility Flash",
  INITIAL_MESSAGE: "Hi, I\u2019m **Agility Flash**, ready to answer your questions!"
};
var CHATBOT_MESSAGES = {
  // Common messages
  CANCELLED: `The message was canceled mid-process and wasn\u2019t handled successfully.`,
  INTERRUPTED: "Action was interrupted",
  HANG_ON: "The system is unresponsive",
  ERROR: "Oops! Something went wrong. Please try again.",
  ERROR_FALLBACK: "Oops! This is error fallback.",
  ABORTED: "Operation was aborted by the user",
  // Kitchen messages
  FURTHER_ACTION: "Your request was completed successfully. Would you like me to handle anything else?",
  MENU_RESULT: `Got it! Here\u2019s what\u2019s on the menu for the time frame you provided`,
  PLAN_RESULT: `Certainly. The following plan corresponds to your specified time frame`,
  FETCH_STATISTIC_SUCCESSFULLY: `\u{1F4CA} Your statistical data for the selected time frame is now available. Let me know if there's anything else you'd like me to take care of.`,
  INVALID_TOTAL_MODE: (totalMode) => `Your given mode ${totalMode} is not valid in current chart view. Please try again.`,
  SET_TOTAL_MODE: (totalMode) => `Your given mode ${totalMode} was set successfully in current chart view. Feel free to reach out if you need further assistance.`,
  SEARCH_SUCCESSFULLY: (action) => `Action ${action} was completed successfully. Let me know if there's anything else you need\u2014I'm here to help. I'll also make sure the relevant details are added to memory.`
};
var LONG_RESPONSE_TIME = 6e4;
var CHATBOT_EVENTS = {
  SIDEBAR_CLOSED_BY_SEARCH: "chatbot:sidebar-closed-by-search"
};
var CHATBOT_SIDEBAR_CLASSES = {
  OPEN: "sidebar-open",
  CLOSE: "sidebar-close"
};

// src/breakpointValues.ts
var breakpointValues = {
  xs: 428,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1440,
  "3xl": 1536
};
export {
  AFTERNOON_END,
  AFTERNOON_START,
  AGE_MAX,
  AGE_MIN,
  API_ERROR_MESSAGE,
  ActionGroup2 as ActionGroup,
  BLOCK_FORMATS,
  BLUR_SRC,
  BREAKPOINTS,
  CERTIFICATE_ISSUER_LENGTH,
  CERTIFICATE_NAME_LENGTH,
  CERTIFICATE_URL_LENGTH,
  CHATBOT_EVENTS,
  CHATBOT_MESSAGES,
  CHATBOT_SIDEBAR_CLASSES,
  CLERK_STATUS_CODE,
  CLERK_STATUS_MESSAGES,
  CONFIRM_MESSAGE_CONFIG,
  COOKIES_TOKEN_SHARE_CONFIG,
  COOKIE_KEYS_SHARED,
  COPILOT_KIT_API_URL,
  COPILOT_LABEL,
  COPYRIGHT,
  CSP_SOURCES,
  CV_TEMPLATE_ID,
  DATE_CONSTANTS,
  DATE_FORMATS,
  DATE_PICKER_DEFAULTS,
  DATE_PICKER_MODES,
  DATE_RANGE_DISPLAY,
  DEBOUNCE_DELAY,
  DEBOUNCE_SEARCH_INPUT,
  DEFAULT_ALLOWED_FORMATS,
  DEFAULT_EMAIL_DOMAIN,
  DEFAULT_EMAIL_DOMAIN_LIST,
  DEFAULT_LIMIT,
  DEFAULT_NAME_CV_TEMPLATE,
  DEFAULT_OFFSET,
  DEFAULT_PROJECTS_LIMIT,
  DEFAULT_RICH_TEXT_VALUE,
  DEFAULT_TRAININGS_LIMIT,
  DEFAULT_USERS_LIMIT,
  DEV_CSP_SOURCES,
  EMPTY_FIELD_VALUE,
  EXTERNAL_SERVICES,
  FALLBACK_SRC,
  FIREBASE_CONFIG,
  FULL_DAY,
  GOOGLE_IMAGE_RESOURCE,
  HALF_DAY,
  HALF_DAY_HOURS,
  HERO_CONTENT,
  IMAGES,
  IMAGE_PROCESSING_MESSAGE,
  INPUT_ADDRESS_LENGTH,
  INPUT_COMPANY_LENGTH,
  INPUT_DEGREE_LENGTH,
  INPUT_JOB_TITLE_LENGTH,
  INPUT_MAJOR_LENGTH,
  INPUT_NAME_USER_LENGTH,
  INPUT_PHONE_NUMBER_LENGTH,
  INPUT_PROJECT_NAME_LENGTH,
  INPUT_ROLE_LENGTH,
  INPUT_SCHOOL_LOCATION_LENGTH,
  INPUT_SCHOOL_NAME_LENGTH,
  INPUT_SOCIAL_NAME_LENGTH,
  INPUT_SOCIAL_URL_LENGTH,
  INPUT_TYPE,
  KEYBOARD_EVENT,
  KEYBOARD_SHORTCUT,
  LONG_RESPONSE_TIME,
  MAPBOX_MESSAGE,
  MAPBOX_REGEX,
  MAX_CHARACTERS,
  MAX_CHATBOT_FILE_SIZE,
  MAX_FILE_SIZE,
  MAX_IMAGE_DIMENSION,
  MAX_IMAGE_SIZE,
  MESSAGES,
  MONTHS,
  MONTHS_FULL,
  MORNING_END,
  MORNING_START,
  MasterCategoryType,
  PLURALIZATION_REGEX,
  PROGRESS_CONFIG,
  PROJECT_CODE_REGEX,
  PROJECT_NAV_LINKS,
  PROJECT_PAGE_MESSAGE,
  PROJECT_TIMELINE_LIMIT,
  QUERY_KEY,
  REGEX_EMAIl,
  REGEX_LATIN_CHARACTERS,
  REGEX_PATTERN,
  REGEX_PHONE_NUMBER,
  REGEX_URL,
  RESUME_MESSAGE,
  ROLE_ITEMS,
  ROUTES,
  ResumeStep,
  SCROLL_CONFIG,
  SEARCH_FORM_HEADING_ICONS,
  SEARCH_FORM_MESSAGE,
  SEARCH_PLACEHOLDER,
  SELECT_CLOSE_DELAY,
  SOCIAL_MEDIA_REGEX,
  STATUS_CODE,
  STATUS_MESSAGES,
  STEP_TO_ROUTE,
  TEXT_FORMATS,
  TEXT_WRAPPING_PATTERNS,
  THEME,
  UNICODE,
  UPLOAD_FILE_MESSAGE,
  USERNAME_PLACEHOLDER_REGEX,
  USER_GROUP_LABEL,
  UserRole,
  UserStatus,
  WORKING_DAYS,
  WORKING_HOURS_PER_DAY,
  WORK_PLACES_TEXT,
  breakpointValues,
  getShortcut
};
